<template>
  <div class="m-mediawall-wrapper" :style="{
    minWidth: `${minContainerWidth}px`
  }">
    <!-- Zoom controls -->
    <div class="zoom-controls">
      <button @click="zoomIn" class="zoom-btn" title="Zoom In">
        <i class="pi pi-plus"></i>
      </button>
      <span class="zoom-level">{{ Math.round(zoom * 100) }}%</span>
      <button @click="zoomOut" class="zoom-btn" title="Zoom Out">
        <i class="pi pi-minus"></i>
      </button>
      <button @click="resetZoom" class="zoom-btn" title="Fit to Screen">
        <i class="pi pi-refresh"></i>
      </button>
    </div>

    <div ref="containerRef" class="m-mediawall-container" @wheel.prevent="handleWheel" @mousedown="handlePanStart"
      @mousemove="handlePanMove" @mouseup="handlePanEnd" @mouseleave="handlePanEnd">
      <div class="m-mediawall" :class="{ 'draggable': dragEnabled && !readonly, 'panning': isPanning }"
        :style="getCanvasTransform()" @dragover.prevent="handleDragOver" @drop.prevent="handleDrop"
        @dragleave="handleDragLeave">
        <!-- Grid background -->
        <div class="m-mediawall-grid">
          <div v-for="(row, y) in gridHeight" :key="`row-${y}`" class="m-mediawall-row">
            <div v-for="(col, x) in gridWidth" :key="`cell-${x}-${y}`" class="m-mediawall-cell"
              :style="getCellStyle()" />
          </div>
        </div>

        <!-- Existing widgets -->
        <div v-for="(widget, index) in widgets" :key="widget.id || index" class="m-mediawall-widget" :class="{
          'dragging': draggingWidget === widget,
          'editable': dragEnabled && !readonly
        }" :style="getWidgetStyle(widget)" :draggable="dragEnabled && !readonly"
          @dragstart="handleWidgetDragStart(widget, $event)" @dragend.prevent="handleWidgetDragEnd"
          @click="handleWidgetClick(widget, $event)" @dblclick.prevent="handleWidgetDoubleClick(widget, $event)"
          @contextmenu.prevent="handleWidgetContextMenu(widget, $event)">
          <div class="widget-content">
            <img v-if="widgetImageUrl(widget.image)" :src="widgetImageUrl(widget.image)" class="widget-image" alt=""
              @error="handleImageError" />
            <div class="widget-title">{{ widget.title }}</div>
          </div>
        </div>

        <!-- Moving/preview widget during drag -->
        <div v-if="movingWidget" class="m-mediawall-widget moving" :class="{
          'valid': placementValid === 'VALID',
          'replaceable': placementValid === 'REPLACEABLE',
          'invalid': placementValid === 'INVALID'
        }" :style="getWidgetStyle(movingWidget)">
          <div class="widget-content">
            <img v-if="widgetImageUrl(movingWidget.image)" :src="widgetImageUrl(movingWidget.image)" class="widget-image" alt=""
              @error="handleImageError" />
            <div class="widget-title">{{ movingWidget.title }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Context menu for widgets -->
    <ContextMenu ref="contextMenu" :model="contextMenuItems" @hide="onMediaWallContextMenuHide" />
  </div>
</template>

<script setup lang="ts">
import { activateContextMenu, deactivateContextMenu } from '@/common/utils/contextMenuCoordinator'
import { getPrimeIcon } from '@/common/utils/iconMapper'
import { resolveSwingIconUrl } from '@/common/utils/resolvePlatformIcon'
import { useAppStore } from '@/stores/app'
import ContextMenu from 'primevue/contextmenu'
import { storeToRefs } from 'pinia'
import { computed, inject, onMounted, ref } from 'vue'

export interface MediaWallWidget {
  id?: string | number
  color: string
  title: string
  width: number  // in grid cells
  height: number // in grid cells
  x: number      // grid x position
  y: number      // grid y position
  image?: string
  payload?: any
}

export interface MediaWallModel {
  gridWidth: number
  gridHeight: number
  widgets: MediaWallWidget[]
}

interface Props {
  modelValue?: MediaWallModel
  readonly?: boolean
  dragEnabled?: boolean
  replaceable?: boolean
  inlineActions?: any[]
  elementId?: string
  panelId?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({ gridWidth: 10, gridHeight: 8, widgets: [] }),
  readonly: false,
  dragEnabled: true,
  replaceable: false,
  inlineActions: () => [],
  elementId: '',
  panelId: ''
})

const emit = defineEmits<{
  'update:modelValue': [model: MediaWallModel]
  'change': [widget: MediaWallWidget]
  'replace': [payload: { widget: MediaWallWidget; replaced: MediaWallWidget[] }]
  'widget-click': [widget: MediaWallWidget]
  'widget-dblclick': [widget: MediaWallWidget]
  'context-menu': [payload: { widget: MediaWallWidget; event: MouseEvent }]
  'drag-start': [widget: MediaWallWidget]
  'drag-end': [widget: MediaWallWidget]
  'import': [data: any]
}>()

// Inject onAction to execute actions
const onAction = inject<((context: any) => void)>('onAction', () => { })
const appStore = useAppStore()
const { currentPlatform } = storeToRefs(appStore)

const containerRef = ref<HTMLElement>()
const contextMenu = ref()
const draggingWidget = ref<MediaWallWidget | null>(null)
const movingWidget = ref<MediaWallWidget | null>(null)
const dragOffset = ref({ x: 0, y: 0 })
const placementValid = ref<'VALID' | 'REPLACEABLE' | 'INVALID'>('INVALID')
const selectedWidget = ref<MediaWallWidget | null>(null)

// Zoom and pan state
const zoom = ref(1)
const panOffset = ref({ x: 0, y: 0 })
const isPanning = ref(false)
const panStart = ref({ x: 0, y: 0 })
const minZoom = 0.1
const maxZoom = 3

const gridWidth = computed(() => props.modelValue?.gridWidth || 10)
const gridHeight = computed(() => props.modelValue?.gridHeight || 8)
const widgets = computed(() => props.modelValue?.widgets || [])

// Calculate minimum container dimensions based on grid size
const minContainerWidth = computed(() => {
  const cellSize = 60
  return Math.max(gridWidth.value * cellSize, 80)
})

// Zoom and pan methods
const getCanvasTransform = () => {
  return {
    transform: `scale(${zoom.value}) translate(${panOffset.value.x}px, ${panOffset.value.y}px)`,
    transformOrigin: '0 0'
  }
}

const zoomIn = () => {
  zoom.value = Math.min(zoom.value * 1.2, maxZoom)
}

const zoomOut = () => {
  zoom.value = Math.max(zoom.value / 1.2, minZoom)
}

const resetZoom = () => {
  if (!containerRef.value) return

  const containerWidth = containerRef.value.clientWidth
  const containerHeight = containerRef.value.clientHeight

  const cellSize = 60
  const gridPixelWidth = gridWidth.value * cellSize
  const gridPixelHeight = gridHeight.value * cellSize

  const scaleX = containerWidth / gridPixelWidth
  const scaleY = containerHeight / gridPixelHeight

  zoom.value = Math.min(scaleX, scaleY, 1) * 0.95
  panOffset.value = { x: 0, y: 0 }
}

const handleWheel = (event: WheelEvent) => {
  if (event.ctrlKey || event.metaKey) {
    const delta = -event.deltaY
    if (delta > 0) {
      zoomIn()
    } else {
      zoomOut()
    }
  }
}

const handlePanStart = (event: MouseEvent) => {
  if (event.button === 1 || (event.button === 0 && event.shiftKey)) {
    isPanning.value = true
    panStart.value = {
      x: event.clientX - panOffset.value.x,
      y: event.clientY - panOffset.value.y
    }
    event.preventDefault()
  }
}

const handlePanMove = (event: MouseEvent) => {
  if (isPanning.value) {
    panOffset.value = {
      x: event.clientX - panStart.value.x,
      y: event.clientY - panStart.value.y
    }
    event.preventDefault()
  }
}

const handlePanEnd = () => {
  isPanning.value = false
}

const getDropDownName = (action: any): string => {
  const dropDown = action.dropDownActionName
  if (Array.isArray(dropDown)) {
    return dropDown[0]?._text?.trim() || ''
  }
  return dropDown?._text?.trim() || ''
}

const isDropDownMain = (action: any): boolean => {
  const dropDown = action.dropDownMain
  const value = Array.isArray(dropDown) ? dropDown[0]?._text : dropDown?._text
  return value === 'true' || value === true
}

// Build context menu items from inline actions with grouping support
const contextMenuItems = computed(() => {
  if (!props.inlineActions || props.inlineActions.length === 0) return []

  // Group actions by dropDownActionName
  const groups: Map<string, any[]> = new Map()
  const ungrouped: any[] = []

  props.inlineActions.forEach(action => {
    const dropDownName = getDropDownName(action)
    if (dropDownName !== '') {
      if (!groups.has(dropDownName)) {
        groups.set(dropDownName, [])
      }
      groups.get(dropDownName)!.push(action)
    } else {
      ungrouped.push(action)
    }
  })

  const result: any[] = []

  // Add ungrouped actions
  ungrouped.forEach(action => {
    result.push({
      label: action.title?._text || action.name?._text || 'Action',
      icon: getPrimeIcon(action.image?._text || ''),
      command: () => executeAction(action)
    })
  })

  // Add grouped actions as nested menus
  groups.forEach((actions, groupName) => {
    const mainAction = actions.find(a => isDropDownMain(a)) || actions[0]

    result.push({
      label: mainAction.title?._text || mainAction.name?._text || groupName,
      icon: getPrimeIcon(mainAction.image?._text || ''),
      items: actions.map(action => ({
        label: action.title?._text || action.name?._text || 'Action',
        icon: getPrimeIcon(action.image?._text || ''),
        command: () => executeAction(action)
      }))
    })
  })

  return result
})

const executeAction = async (action: any) => {
  const platform = action.platform?._text || currentPlatform.value || undefined

  if (platform) {
    appStore.updateCurrentPlatform(platform)
  }

  const ctx = {
    id: action._attributes ? +action._attributes.id : 0,
    name: action.name?._text || '',
    elementId: props.elementId,
    panelId: props.panelId,
    level: 'element',
    data: action,
    platform
  }

  await onAction({ getContext: () => ctx })
}

const getCellSize = () => {
  if (!containerRef.value) return { width: 60, height: 60 }
  const containerWidth = containerRef.value.clientWidth
  const containerHeight = containerRef.value.clientHeight

  // Minimum cell size for responsiveness
  const minCellWidth = 60
  const minCellHeight = 60

  return {
    width: Math.max(containerWidth / gridWidth.value, minCellWidth),
    height: Math.max(containerHeight / gridHeight.value, minCellHeight)
  }
}

const getCellStyle = () => {
  const size = getCellSize()
  return {
    width: `${size.width}px`,
    height: `${size.height}px`,
    minWidth: '60px',
    minHeight: '60px'
  }
}

const getWidgetStyle = (widget: MediaWallWidget) => {
  const size = getCellSize()
  return {
    backgroundColor: widget.color,
    left: `${widget.x * size.width}px`,
    top: `${widget.y * size.height}px`,
    width: `${widget.width * size.width}px`,
    height: `${widget.height * size.height}px`
  }
}

const isValidPlacement = (widget: MediaWallWidget): boolean => {
  // Check if widget is within grid bounds
  if (widget.x < 0 || widget.y < 0) return false
  if (widget.x + widget.width > gridWidth.value) return false
  if (widget.y + widget.height > gridHeight.value) return false
  return true
}

const getIntersectingWidgets = (widget: MediaWallWidget): MediaWallWidget[] => {
  const intersecting: MediaWallWidget[] = []
  for (const existing of widgets.value) {
    if (existing === draggingWidget.value) continue

    // Check intersection
    if (!(widget.x >= existing.x + existing.width ||
      widget.x + widget.width <= existing.x ||
      widget.y >= existing.y + existing.height ||
      widget.y + widget.height <= existing.y)) {
      intersecting.push(existing)
    }
  }
  return intersecting
}

const validatePlacement = (widget: MediaWallWidget): 'VALID' | 'REPLACEABLE' | 'INVALID' => {
  if (!isValidPlacement(widget)) {
    return 'INVALID'
  }

  const intersecting = getIntersectingWidgets(widget)
  if (intersecting.length === 0) {
    return 'VALID'
  }

  if (props.replaceable) {
    return 'REPLACEABLE'
  }

  return 'INVALID'
}

const getGridPosition = (clientX: number, clientY: number) => {
  if (!containerRef.value) return { x: 0, y: 0 }

  const rect = containerRef.value.getBoundingClientRect()
  const size = getCellSize()

  // Account for zoom and pan transformations
  const adjustedX = (clientX - rect.left - panOffset.value.x) / zoom.value
  const adjustedY = (clientY - rect.top - panOffset.value.y) / zoom.value

  const x = Math.floor(adjustedX / size.width)
  const y = Math.floor(adjustedY / size.height)

  return { x, y }
}

// Widget drag handling
const handleWidgetDragStart = (widget: MediaWallWidget, event: DragEvent) => {
  if (!props.dragEnabled || props.readonly) {
    event.preventDefault()
    return
  }

  draggingWidget.value = widget

  const pos = getGridPosition(event.clientX, event.clientY)
  dragOffset.value = {
    x: pos.x - widget.x,
    y: pos.y - widget.y
  }

  // Set drag image (invisible)
  if (event.dataTransfer) {
    const img = new Image()
    img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
    event.dataTransfer.setDragImage(img, 0, 0)
    event.dataTransfer.effectAllowed = 'move'
  }

  // Create moving widget preview
  movingWidget.value = { ...widget }

  // Emit drag-start event
  emit('drag-start', widget)
}

const handleWidgetDragEnd = (event?: DragEvent) => {
  if (event) {
    event.preventDefault()
  }

  if (!movingWidget.value || !draggingWidget.value) {
    cleanup()
    return
  }

  const validation = validatePlacement(movingWidget.value)

  try {
    if (validation === 'VALID' || validation === 'REPLACEABLE') {
      const newWidgets = [...widgets.value]
      const index = newWidgets.findIndex(w => w === draggingWidget.value)

      if (index !== -1) {
        // Check if position actually changed
        const positionChanged =
          newWidgets[index].x !== movingWidget.value.x ||
          newWidgets[index].y !== movingWidget.value.y

        if (positionChanged) {
          // Update widget position
          const updatedWidget = {
            ...newWidgets[index],
            x: movingWidget.value.x,
            y: movingWidget.value.y
          }
          newWidgets[index] = updatedWidget

          if (validation === 'REPLACEABLE') {
            const intersecting = getIntersectingWidgets(movingWidget.value)
            // Remove intersecting widgets
            const filtered = newWidgets.filter(w => !intersecting.includes(w))

            emit('update:modelValue', {
              ...props.modelValue!,
              widgets: filtered
            })
            emit('replace', { widget: updatedWidget, replaced: intersecting })
          } else {
            emit('update:modelValue', {
              ...props.modelValue!,
              widgets: newWidgets
            })
            emit('change', updatedWidget)
          }
        }
      }
    }
  } catch (error) {
    console.error('Error in handleWidgetDragEnd:', error)
  } finally {
    // Emit drag-end event
    if (draggingWidget.value) {
      emit('drag-end', draggingWidget.value)
    }
    cleanup()
  }
}

const cleanup = () => {
  draggingWidget.value = null
  movingWidget.value = null
  placementValid.value = 'INVALID'
}

// External drag & drop handling
const handleDragOver = (event: DragEvent) => {
  if (!props.dragEnabled || props.readonly) return

  const pos = getGridPosition(event.clientX, event.clientY)

  if (draggingWidget.value && movingWidget.value) {
    // Moving existing widget
    movingWidget.value = {
      ...movingWidget.value,
      x: pos.x - dragOffset.value.x,
      y: pos.y - dragOffset.value.y
    }
    placementValid.value = validatePlacement(movingWidget.value)
  }
}

const handleDrop = (event: DragEvent) => {
  if (!props.dragEnabled || props.readonly) return
  event.preventDefault()
  // Drop is handled in handleWidgetDragEnd for existing widgets
}

const handleDragLeave = () => {
  // Optional: handle visual feedback when drag leaves container
}

// Widget interaction
const handleWidgetClick = (widget: MediaWallWidget, event: MouseEvent) => {
  event.stopPropagation()
  selectedWidget.value = widget
  emit('widget-click', widget)
}

const handleWidgetDoubleClick = (widget: MediaWallWidget, event: MouseEvent) => {
  event.stopPropagation()
  selectedWidget.value = widget
  emit('widget-dblclick', widget)

  // Execute first inline action if available
  if (props.inlineActions && props.inlineActions.length > 0) {
    executeAction(props.inlineActions[0])
  }
}

const handleWidgetContextMenu = (widget: MediaWallWidget, event: MouseEvent) => {
  event.stopPropagation()
  selectedWidget.value = widget
  emit('context-menu', { widget, event })

  // Show context menu if we have inline actions
  if (contextMenu.value && props.inlineActions && props.inlineActions.length > 0) {
    activateContextMenu(contextMenu.value, event.target)
    contextMenu.value.show(event)
  }
}

const onMediaWallContextMenuHide = () => {
  if (contextMenu.value) {
    deactivateContextMenu(contextMenu.value)
  }
}

const widgetImageUrl = (image?: string): string => resolveSwingIconUrl(image ?? '') ?? ''

const handleImageError = (event: Event) => {
  // Hide broken images
  const img = event.target as HTMLImageElement
  if (img) {
    img.style.display = 'none'
  }
}

// Initialize zoom to fit on mount
onMounted(() => {
  setTimeout(() => {
    resetZoom()
  }, 100)
})
</script>

<style scoped lang="scss">
.m-mediawall-wrapper {
  width: 100%;
  height: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #ffffff;
  position: relative;
  display: flex;
  flex-direction: column;
}

.zoom-controls {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1000;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  background: white;
  padding: 0.5rem;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.zoom-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }

  &:active {
    background: #e5e7eb;
  }

  i {
    font-size: 0.875rem;
  }
}

.zoom-level {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
  min-width: 50px;
  text-align: center;
}

.m-mediawall-container {
  flex: 1;
  overflow: auto;
  position: relative;
  background: #f9fafb;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.m-mediawall {
  position: relative;
  min-width: 100%;
  min-height: 100%;
  background: #ffffff;
  transform-origin: 0 0;
  transition: transform 0.1s ease-out;

  &.draggable {
    cursor: default;
  }

  &.panning {
    cursor: grabbing;
  }
}

.m-mediawall-grid {
  display: flex;
  flex-direction: column;
}

.m-mediawall-row {
  display: flex;
}

.m-mediawall-cell {
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: #f9fafb;
  transition: background-color 0.15s;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
  }
}

.m-mediawall-widget {
  position: absolute;
  border: 2px solid rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s, box-shadow 0.15s;
  cursor: pointer;
  user-select: none;
  overflow: hidden;

  &.editable {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  &:hover:not(.moving) {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10;
  }

  &.dragging {
    opacity: 0.5;
  }

  &.moving {
    opacity: 0.8;
    pointer-events: none;
    z-index: 100;

    &.valid {
      border-color: #10b981;
      box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.3);
    }

    &.replaceable {
      border-color: #f59e0b;
      box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.3);
    }

    &.invalid {
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.3);
    }
  }
}

.widget-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  position: relative;
  overflow: hidden;
}

.widget-image {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  max-width: 24px;
  max-height: 24px;
  object-fit: contain;
}

.widget-title {
  color: #000;
  font-weight: 500;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  font-size: clamp(0.625rem, 1.5vw, 0.875rem);
  line-height: 1.3;
  max-width: 100%;
  padding: 0.25rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

// Responsive - min-width is now dynamically set via inline style</style>
